import { useEffect, useState } from "react"
import { motion, AnimatePresence } from "motion/react"

// Right-hand cinematic panel for sign-in / sign-up. A working diagram of
// the product's intelligence: a hypercar wireframe, a slow radar sweep,
// damage zones being identified one at a time with annotations, and a
// neural readout panel showing live metrics + a continuously-cycling
// thinking trace ("> Detecting impact zones...") that telegraphs the
// AI is *reasoning*, not just displaying numbers.
//
// All visuals: pure SVG + motion + CSS. Zero external assets. Zero new
// dependencies. Sub-millisecond initial paint. Motion is GPU-only.

interface AuthHeroPanelProps {
  /** Subtle differentiation between sign-in and sign-up — different
   *  starting damage zone + different starting thinking-trace step. */
  variant?: "signin" | "signup"
}

interface DamageZone {
  id: string
  label: string
  // Dot position inside the 1000×500 vehicle SVG viewBox.
  x: number
  y: number
  // Where the callout label box sits, relative to the dot.
  labelDx: number
  labelDy: number
  severity: number
  hours: number
}

// Label offsets are tuned to keep every callout box out of the top-right
// corner (where the neural readout panel sits), and inside the viewBox.
const ZONES: DamageZone[] = [
  { id: "splitter", label: "Front splitter", x: 130, y: 348, labelDx: 30, labelDy: -130, severity: 0.42, hours: 3.5 },
  { id: "hood", label: "Hood", x: 320, y: 290, labelDx: -130, labelDy: -90, severity: 0.58, hours: 4.2 },
  { id: "fender", label: "L. fender", x: 250, y: 320, labelDx: 30, labelDy: 120, severity: 0.31, hours: 2.0 },
  { id: "door", label: "Door", x: 550, y: 270, labelDx: 120, labelDy: 100, severity: 0.18, hours: 1.5 },
  { id: "intake", label: "Side intake", x: 685, y: 320, labelDx: 100, labelDy: 110, severity: 0.22, hours: 1.8 },
  { id: "quarter", label: "Quarter panel", x: 820, y: 295, labelDx: -160, labelDy: 100, severity: 0.66, hours: 5.5 },
  { id: "diffuser", label: "Rear diffuser", x: 905, y: 365, labelDx: -120, labelDy: 90, severity: 0.51, hours: 3.8 },
]

// Thinking trace — each line is one reasoning step the model takes when
// turning a photo into an estimate. Cycles continuously. Reads as a
// real AI debug log; non-technical users perceive it as "the machine is
// thinking right now."
const TRACE_LINES = [
  "Detecting impact zones · 7 found",
  "Segmenting body panels · OK",
  "Querying OEM library · Honda Accord 2022",
  "Reasoning over structural integrity…",
  "Estimating labor · 4.6h",
  "Computing parts cost · $1,840",
  "Cross-checking aluminum thresholds…",
  "Aggregating final estimate…",
  "Confidence consensus · 0.92",
]

const TAGLINE = "Estimates from a single photo"
const SUBTAG = "Vision AI scopes the damage. A reasoning model prices it. You ship the quote."

// Static positions for a scattered field of "analysis points" — gives the
// background a sense of awareness/perception across the whole canvas
// without overwhelming. Each pulses on its own period so they never
// align rhythmically.
const SCATTER = [
  { cx: 12, cy: 22, period: 4.2 },
  { cx: 24, cy: 14, period: 5.4 },
  { cx: 38, cy: 9, period: 6.1 },
  { cx: 71, cy: 11, period: 4.8 },
  { cx: 86, cy: 18, period: 5.2 },
  { cx: 92, cy: 36, period: 4.5 },
  { cx: 8, cy: 48, period: 5.8 },
  { cx: 10, cy: 78, period: 4.6 },
  { cx: 91, cy: 72, period: 5.1 },
  { cx: 78, cy: 88, period: 4.4 },
  { cx: 28, cy: 90, period: 5.6 },
  { cx: 52, cy: 8, period: 6.0 },
]

export function AuthHeroPanel({ variant = "signin" }: AuthHeroPanelProps) {
  return (
    <section className="hidden md:block flex-1 relative p-4">
      <div className="animate-slide-right animate-delay-300 absolute inset-4 rounded-3xl overflow-hidden bg-[#05070a]">
        <Backdrop />
        <div className="absolute inset-0 grid place-items-center px-10">
          <VehicleSurface variant={variant} />
        </div>
        <TopEdge />
        <BottomEdge />
        <NeuralReadout variant={variant} />
      </div>
    </section>
  )
}

/* ── Backdrop ──────────────────────────────────────────── */

function Backdrop() {
  return (
    <>
      {/* Base — graphite with two emerald sources giving directional light. */}
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(120% 80% at 75% 0%, rgba(16,185,129,0.16), transparent 55%), radial-gradient(80% 60% at 20% 100%, rgba(16,185,129,0.08), transparent 60%), #05070a",
        }}
      />

      {/* Engineered grid */}
      <svg
        aria-hidden
        className="absolute inset-0 w-full h-full opacity-[0.18]"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <pattern id="auth-grid" width="24" height="24" patternUnits="userSpaceOnUse">
            <path d="M 24 0 L 0 0 0 24" fill="none" stroke="rgba(16,185,129,0.32)" strokeWidth="0.4" />
          </pattern>
          <pattern id="auth-grid-major" width="120" height="120" patternUnits="userSpaceOnUse">
            <path d="M 120 0 L 0 0 0 120" fill="none" stroke="rgba(16,185,129,0.5)" strokeWidth="0.6" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#auth-grid)" />
        <rect width="100%" height="100%" fill="url(#auth-grid-major)" />
      </svg>

      {/* Field of analysis points — small dots scattered across the canvas
          with independent slow pulses. Each one feels like a perception
          probe; together they read as "the model is paying attention to
          the whole frame." */}
      <svg
        aria-hidden
        className="absolute inset-0 w-full h-full pointer-events-none"
        preserveAspectRatio="none"
        viewBox="0 0 100 100"
      >
        {SCATTER.map((p, i) => (
          <g key={i}>
            <circle
              cx={p.cx}
              cy={p.cy}
              r="0.18"
              fill="rgba(16,185,129,0.55)"
            >
              <animate
                attributeName="opacity"
                values="0.15;0.55;0.15"
                dur={`${p.period}s`}
                repeatCount="indefinite"
              />
              <animate
                attributeName="r"
                values="0.18;0.32;0.18"
                dur={`${p.period}s`}
                repeatCount="indefinite"
              />
            </circle>
            {/* Faint connecting tick — gestures at "node in a graph" */}
            <line
              x1={p.cx}
              y1={p.cy - 0.4}
              x2={p.cx}
              y2={p.cy + 0.4}
              stroke="rgba(16,185,129,0.18)"
              strokeWidth="0.04"
            />
          </g>
        ))}
      </svg>

      {/* Perspective lines vanishing toward off-screen point — depth
          without literal floor/wall. */}
      <svg
        aria-hidden
        className="absolute inset-0 w-full h-full opacity-[0.10] pointer-events-none"
        preserveAspectRatio="none"
        viewBox="0 0 100 100"
      >
        <line x1="0" y1="100" x2="100" y2="0" stroke="rgba(16,185,129,0.6)" strokeWidth="0.06" strokeDasharray="0.4 0.6" />
        <line x1="0" y1="80" x2="100" y2="10" stroke="rgba(16,185,129,0.45)" strokeWidth="0.05" strokeDasharray="0.3 0.7" />
        <line x1="0" y1="60" x2="100" y2="22" stroke="rgba(16,185,129,0.35)" strokeWidth="0.04" strokeDasharray="0.3 0.7" />
      </svg>

      {/* Subtle horizontal "energy bands" — three faint horizontal lines
          that drift in opacity. Only barely visible but adds a layer of
          atmosphere. */}
      {[0.35, 0.55, 0.78].map((y, i) => (
        <motion.div
          key={i}
          aria-hidden
          animate={{ opacity: [0.04, 0.10, 0.04] }}
          transition={{ duration: 8 + i * 2, repeat: Infinity, ease: "easeInOut" }}
          className="absolute left-0 right-0 h-px"
          style={{
            top: `${y * 100}%`,
            background:
              "linear-gradient(90deg, transparent 0%, rgba(16,185,129,0.6) 35%, rgba(16,185,129,0.6) 65%, transparent 100%)",
          }}
        />
      ))}

      {/* Edge vignette */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{ boxShadow: "inset 0 0 220px 50px rgba(0,0,0,0.65)" }}
      />
    </>
  )
}

/* ── Vehicle surface ────────────────────────────────────── */

const FRONT_WHEEL = { cx: 220, cy: 380 }
const REAR_WHEEL = { cx: 800, cy: 380 }
const WHEEL_R_OUTER = 62
const WHEEL_R_RIM = 44
const SPOKES = [18, 90, 162, 234, 306].map((d) => (d * Math.PI) / 180)

function VehicleSurface({ variant }: { variant: "signin" | "signup" }) {
  const [active, setActive] = useState<number>(variant === "signup" ? 3 : 0)
  useEffect(() => {
    const id = setInterval(() => {
      setActive((i) => (i + 1) % ZONES.length)
    }, 2400)
    return () => clearInterval(id)
  }, [])

  return (
    <svg
      viewBox="0 0 1000 500"
      className="w-full max-w-[820px] drop-shadow-[0_24px_64px_rgba(16,185,129,0.20)]"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id="body-stroke" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="rgba(16,185,129,0.85)" />
          <stop offset="0.5" stopColor="rgba(16,185,129,1)" />
          <stop offset="1" stopColor="rgba(16,185,129,0.85)" />
        </linearGradient>
        <linearGradient id="body-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="rgba(16,185,129,0.10)" />
          <stop offset="1" stopColor="rgba(16,185,129,0.02)" />
        </linearGradient>
        <radialGradient id="radar-fade" cx="0" cy="0.5" r="1">
          <stop offset="0" stopColor="rgba(16,185,129,0.34)" />
          <stop offset="1" stopColor="rgba(16,185,129,0)" />
        </radialGradient>
        <radialGradient id="floor-glow" cx="0.5" cy="0" r="0.5">
          <stop offset="0" stopColor="rgba(16,185,129,0.4)" />
          <stop offset="1" stopColor="rgba(16,185,129,0)" />
        </radialGradient>
      </defs>

      {/* Floor light pool */}
      <ellipse cx="510" cy="442" rx="380" ry="22" fill="url(#floor-glow)" opacity="0.85" />

      {/* Concentric range arcs + tick marks */}
      <g stroke="rgba(16,185,129,0.22)" strokeWidth="0.5" fill="none" strokeDasharray="2 4">
        <circle cx="510" cy="350" r="240" />
        <circle cx="510" cy="350" r="340" />
        <circle cx="510" cy="350" r="440" />
      </g>
      <g stroke="rgba(16,185,129,0.4)" strokeWidth="0.7">
        {Array.from({ length: 12 }).map((_, i) => {
          const a = (i * 30 - 90) * (Math.PI / 180)
          if (a < -Math.PI || a > 0) return null
          const x1 = 510 + 440 * Math.cos(a)
          const y1 = 350 + 440 * Math.sin(a)
          const x2 = 510 + 432 * Math.cos(a)
          const y2 = 350 + 432 * Math.sin(a)
          return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} />
        })}
      </g>

      {/* Radar sweep wedge */}
      <motion.g
        animate={{ rotate: 360 }}
        transition={{ duration: 7, repeat: Infinity, ease: "linear" }}
        style={{ transformOrigin: "510px 350px", transformBox: "fill-box" }}
      >
        <path d="M 510 350 L 950 350 A 440 440 0 0 0 905 130 Z" fill="url(#radar-fade)" />
        <line x1="510" y1="350" x2="950" y2="350" stroke="rgba(16,185,129,0.6)" strokeWidth="0.9" />
      </motion.g>

      {/* Hypercar wireframe */}
      <g>
        <path
          d="
            M 120 350
            L 90 320
            Q 95 295, 120 285
            L 165 268
            Q 220 250, 300 245
            L 405 250
            L 530 215
            L 600 195
            L 705 200
            L 745 218
            L 800 248
            L 870 290
            L 905 312
            L 920 340
            L 928 380
            L 880 408
            L 870 408
            A 62 62 0 0 0 730 408
            L 310 408
            A 62 62 0 0 0 170 408
            L 130 408
            L 120 380
            Z
          "
          fill="url(#body-fill)"
          stroke="url(#body-stroke)"
          strokeWidth="1.6"
          strokeLinejoin="round"
        />

        {/* Greenhouse */}
        <path
          d="M 530 215 L 575 188 L 705 188 L 745 218"
          fill="rgba(16,185,129,0.06)"
          stroke="rgba(16,185,129,0.7)"
          strokeWidth="1.1"
        />
        <line x1="530" y1="215" x2="575" y2="188" stroke="rgba(16,185,129,0.5)" strokeWidth="0.7" />
        <line x1="705" y1="188" x2="745" y2="218" stroke="rgba(16,185,129,0.5)" strokeWidth="0.7" />
        <line x1="595" y1="188" x2="685" y2="188" stroke="rgba(16,185,129,0.32)" strokeWidth="0.4" strokeDasharray="2 3" />

        {/* Door cut */}
        <path
          d="M 600 196 Q 595 280, 590 350"
          fill="none"
          stroke="rgba(16,185,129,0.42)"
          strokeWidth="0.7"
          strokeDasharray="3 3"
        />
        {/* Side aero crease */}
        <path
          d="M 175 290 Q 380 285, 600 280 Q 740 285, 870 295"
          fill="none"
          stroke="rgba(16,185,129,0.42)"
          strokeWidth="0.7"
        />
        {/* Lower side skirt */}
        <line
          x1="170"
          y1="370"
          x2="870"
          y2="370"
          stroke="rgba(16,185,129,0.32)"
          strokeWidth="0.6"
          strokeDasharray="2 4"
        />

        {/* Hood vent */}
        <g stroke="rgba(16,185,129,0.55)" strokeWidth="0.7" fill="none">
          <path d="M 280 268 L 360 258 L 360 254 L 280 264 Z" />
          <path d="M 290 274 L 360 266" />
        </g>

        {/* Side air intake */}
        <path
          d="M 670 340 L 740 318 L 745 348 Z"
          fill="rgba(16,185,129,0.20)"
          stroke="rgba(16,185,129,0.7)"
          strokeWidth="0.8"
        />
        <g stroke="rgba(16,185,129,0.5)" strokeWidth="0.5">
          <line x1="685" y1="338" x2="725" y2="328" />
          <line x1="694" y1="342" x2="730" y2="335" />
        </g>

        {/* Front splitter + canard */}
        <path
          d="M 95 360 L 165 358 L 165 362 L 95 364 Z"
          fill="rgba(16,185,129,0.18)"
          stroke="rgba(16,185,129,0.7)"
          strokeWidth="0.8"
        />
        <line x1="120" y1="345" x2="160" y2="338" stroke="rgba(16,185,129,0.55)" strokeWidth="0.7" />

        {/* LED DRL */}
        <path
          d="M 105 305 L 175 280 L 175 274 L 105 298 Z"
          fill="rgba(16,185,129,0.85)"
          stroke="rgba(16,185,129,1)"
          strokeWidth="0.4"
        />
        {/* LED taillight */}
        <path
          d="M 825 305 L 920 305 L 920 311 L 825 311 Z"
          fill="rgba(16,185,129,0.85)"
          stroke="rgba(16,185,129,1)"
          strokeWidth="0.4"
        />

        {/* Active rear wing */}
        <g>
          <path
            d="M 760 252 L 870 252 L 870 246 L 760 246 Z"
            fill="rgba(16,185,129,0.18)"
            stroke="rgba(16,185,129,0.7)"
            strokeWidth="0.8"
          />
          <line x1="760" y1="252" x2="765" y2="278" stroke="rgba(16,185,129,0.6)" strokeWidth="0.7" />
          <line x1="870" y1="252" x2="867" y2="278" stroke="rgba(16,185,129,0.6)" strokeWidth="0.7" />
          <line x1="785" y1="252" x2="785" y2="270" stroke="rgba(16,185,129,0.4)" strokeWidth="0.5" />
          <line x1="845" y1="252" x2="845" y2="270" stroke="rgba(16,185,129,0.4)" strokeWidth="0.5" />
        </g>

        {/* Rear diffuser fins */}
        <g>
          <line x1="855" y1="408" x2="855" y2="442" stroke="rgba(16,185,129,0.55)" strokeWidth="1" />
          <line x1="880" y1="408" x2="880" y2="438" stroke="rgba(16,185,129,0.45)" strokeWidth="0.8" />
          <line x1="905" y1="408" x2="905" y2="434" stroke="rgba(16,185,129,0.35)" strokeWidth="0.7" />
          <line x1="925" y1="408" x2="925" y2="430" stroke="rgba(16,185,129,0.25)" strokeWidth="0.6" />
        </g>

        {/* Wheels */}
        {[FRONT_WHEEL, REAR_WHEEL].map((w) => (
          <g key={w.cx}>
            <circle
              cx={w.cx}
              cy={w.cy}
              r={WHEEL_R_OUTER}
              fill="rgba(5,7,10,0.75)"
              stroke="url(#body-stroke)"
              strokeWidth="1.5"
            />
            <circle
              cx={w.cx}
              cy={w.cy}
              r={WHEEL_R_RIM}
              fill="none"
              stroke="rgba(16,185,129,0.6)"
              strokeWidth="0.9"
            />
            <circle
              cx={w.cx}
              cy={w.cy}
              r={WHEEL_R_RIM - 12}
              fill="none"
              stroke="rgba(16,185,129,0.32)"
              strokeWidth="0.5"
              strokeDasharray="2 3"
            />
            {SPOKES.map((a) => (
              <line
                key={a}
                x1={w.cx + 10 * Math.cos(a)}
                y1={w.cy + 10 * Math.sin(a)}
                x2={w.cx + (WHEEL_R_RIM - 2) * Math.cos(a)}
                y2={w.cy + (WHEEL_R_RIM - 2) * Math.sin(a)}
                stroke="rgba(16,185,129,0.65)"
                strokeWidth="1.3"
                strokeLinecap="round"
              />
            ))}
            <circle cx={w.cx} cy={w.cy} r="9" fill="rgba(16,185,129,0.9)" />
            <circle cx={w.cx} cy={w.cy} r="3.5" fill="rgba(5,7,10,0.95)" />
            <circle
              cx={w.cx}
              cy={w.cy}
              r={WHEEL_R_RIM - 5}
              fill="none"
              stroke="rgba(16,185,129,0.18)"
              strokeWidth="0.4"
            />
          </g>
        ))}
      </g>

      {/* Damage callouts (one at a time) */}
      <AnimatePresence mode="wait">
        <DamageCallout key={ZONES[active].id} zone={ZONES[active]} />
      </AnimatePresence>

      {/* Faint dot markers on every zone */}
      <g>
        {ZONES.map((z, i) => {
          const isActive = i === active
          return (
            <circle
              key={z.id}
              cx={z.x}
              cy={z.y}
              r="3"
              fill={isActive ? "rgb(16,185,129)" : "rgba(16,185,129,0.45)"}
            >
              {!isActive && (
                <animate
                  attributeName="opacity"
                  values="0.45;0.15;0.45"
                  dur={`${4 + (i % 3)}s`}
                  repeatCount="indefinite"
                />
              )}
            </circle>
          )
        })}
      </g>

      {/* Horizontal scan line */}
      <motion.line
        initial={{ y: 0 }}
        animate={{ y: [0, 280, 0] }}
        transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
        x1="60"
        x2="940"
        y1="180"
        y2="180"
        stroke="rgba(16,185,129,0.5)"
        strokeWidth="0.9"
        strokeDasharray="4 6"
        style={{ filter: "drop-shadow(0 0 4px rgba(16,185,129,0.7))" }}
      />
    </svg>
  )
}

/* ── Damage callout ─────────────────────────────────────── */

function DamageCallout({ zone }: { zone: DamageZone }) {
  const labelX = zone.x + zone.labelDx
  const labelY = zone.y + zone.labelDy
  const sevPct = Math.round(zone.severity * 100)
  return (
    <motion.g
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
    >
      <circle cx={zone.x} cy={zone.y} r="6" fill="rgb(16,185,129)" />
      <circle
        cx={zone.x}
        cy={zone.y}
        r="6"
        fill="none"
        stroke="rgb(16,185,129)"
        strokeWidth="1"
        opacity="0.5"
      >
        <animate attributeName="r" values="6;20;6" dur="1.8s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="0.6;0;0.6" dur="1.8s" repeatCount="indefinite" />
      </circle>

      <motion.line
        x1={zone.x}
        y1={zone.y}
        x2={labelX}
        y2={labelY}
        stroke="rgba(16,185,129,0.7)"
        strokeWidth="0.9"
        strokeDasharray="3 3"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      />

      <g transform={`translate(${labelX - (zone.labelDx > 0 ? 0 : 178)}, ${labelY - 32})`}>
        <rect
          x="0"
          y="0"
          width="178"
          height="64"
          rx="6"
          fill="rgba(5,7,10,0.88)"
          stroke="rgba(16,185,129,0.55)"
          strokeWidth="0.9"
        />
        <rect x="0" y="0" width="178" height="2" fill="rgb(16,185,129)" />
        <text
          x="10"
          y="18"
          fill="rgba(255,255,255,0.95)"
          fontSize="11"
          fontWeight="700"
          letterSpacing="0.04em"
          style={{ textTransform: "uppercase", fontFamily: "var(--font-sans, system-ui)" }}
        >
          {zone.label}
        </text>
        <text
          x="10"
          y="38"
          fill="rgba(16,185,129,0.9)"
          fontSize="9.5"
          fontWeight="600"
          letterSpacing="0.12em"
          style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}
        >
          SEV {sevPct.toString().padStart(2, "0")} · {zone.hours.toFixed(1)}h
        </text>
        <text
          x="10"
          y="54"
          fill="rgba(255,255,255,0.55)"
          fontSize="9"
          letterSpacing="0.08em"
          style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}
        >
          analyzed · cv-v3
        </text>
      </g>
    </motion.g>
  )
}

/* ── Top edge ───────────────────────────────────────────── */

function TopEdge() {
  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
      className="absolute top-7 left-7 flex items-center gap-2.5"
    >
      <div className="size-10 rounded-xl bg-emerald-500/10 backdrop-blur-md ring-1 ring-emerald-500/30 grid place-items-center">
        <span
          className="text-[13px] font-black text-white tracking-tight"
          style={{ fontFamily: "var(--font-syne)" }}
        >
          eT<span className="text-emerald-400">X</span>
        </span>
      </div>
      <span
        className="text-[14px] font-black text-white tracking-tight hidden lg:inline"
        style={{ fontFamily: "var(--font-syne)" }}
      >
        Impa<span className="text-emerald-400">X</span>t
      </span>
    </motion.div>
  )
}

/* ── Bottom edge ────────────────────────────────────────── */

function BottomEdge() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className="absolute bottom-7 left-7 right-7 flex items-end justify-between gap-6"
    >
      <div className="max-w-md">
        <p
          className="text-[10px] font-bold uppercase tracking-[0.22em] text-emerald-400/75 mb-2"
          style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}
        >
          AI · Collision estimation
        </p>
        <h2 className="text-[26px] font-bold text-white tracking-tight leading-tight">{TAGLINE}</h2>
        <p className="text-[12px] text-white/55 mt-2 leading-relaxed">{SUBTAG}</p>
      </div>
      <div
        className="hidden xl:flex items-center gap-6 text-[10px] tabular-nums text-white/45 pb-1"
        style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}
      >
        <span>cv-v3.2</span>
        <span>·</span>
        <span>SLA &lt; 60s</span>
        <span>·</span>
        <span>OEM-aware</span>
      </div>
    </motion.div>
  )
}

/* ── Neural readout ─────────────────────────────────────── */

function NeuralReadout({ variant }: { variant: "signin" | "signup" }) {
  const [hours, setHours] = useState(4.6)
  const [severity, setSeverity] = useState(0.72)
  const [estimate, setEstimate] = useState(3420)
  const [confidence, setConfidence] = useState(92)
  const [traceIdx, setTraceIdx] = useState(variant === "signup" ? 4 : 0)

  useEffect(() => {
    const tickH = setInterval(() => {
      setHours((h) => clamp(h + (Math.random() - 0.5) * 0.4, 3.8, 5.4))
    }, 800)
    const tickS = setInterval(() => {
      setSeverity((s) => clamp(s + (Math.random() - 0.5) * 0.04, 0.62, 0.84))
    }, 1100)
    const tickE = setInterval(() => {
      setEstimate((e) => Math.round(clamp(e + (Math.random() - 0.5) * 80, 3260, 3580)))
    }, 950)
    const tickC = setInterval(() => {
      setConfidence((c) => Math.round(clamp(c + (Math.random() - 0.5) * 2, 88, 96)))
    }, 1700)
    const tickT = setInterval(() => {
      setTraceIdx((i) => (i + 1) % TRACE_LINES.length)
    }, 1700)
    return () => {
      clearInterval(tickH)
      clearInterval(tickS)
      clearInterval(tickE)
      clearInterval(tickC)
      clearInterval(tickT)
    }
  }, [])

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, delay: 0.55, ease: [0.16, 1, 0.3, 1] }}
      className="absolute top-7 right-7 hidden lg:block"
    >
      <div
        className="rounded-xl border border-emerald-500/30 bg-[#05070a]/85 backdrop-blur-sm w-[260px] shadow-2xl shadow-emerald-900/25 overflow-hidden"
        style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-3.5 py-2.5 border-b border-emerald-500/15 bg-emerald-500/[0.04]">
          <div className="flex items-center gap-2">
            <span className="relative flex size-1.5">
              <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
              <span className="relative inline-flex rounded-full size-1.5 bg-emerald-400" />
            </span>
            <p className="text-[9.5px] font-bold uppercase tracking-[0.22em] text-emerald-400">
              Neural readout
            </p>
          </div>
          <p className="text-[9px] text-emerald-400/70 tabular-nums">cv·v3.2</p>
        </div>

        {/* 2×2 metrics grid */}
        <div className="grid grid-cols-2 gap-x-4 gap-y-3 px-3.5 py-3 border-b border-emerald-500/15">
          <Reading label="Repair" value={hours.toFixed(1)} unit="h" />
          <Reading label="Severity" value={severity.toFixed(2)} />
          <Reading label="Estimate" value={`$${estimate.toLocaleString()}`} />
          <Reading label="Conf." value={`${confidence}`} unit="%" />
        </div>

        {/* Thinking trace — the strongest "intelligence" signal. One line
            of the model's reasoning at a time, fading as it cycles. Reads
            like a real AI debug log. */}
        <div className="px-3.5 py-2.5 min-h-[44px]">
          <p className="text-[8.5px] font-bold uppercase tracking-[0.22em] text-emerald-400/55 mb-1">
            Reasoning
          </p>
          <AnimatePresence mode="wait">
            <motion.p
              key={traceIdx}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.35 }}
              className="text-[10.5px] text-white/85 leading-tight flex items-baseline gap-1.5"
            >
              <span className="text-emerald-400">{">"}</span>
              <span className="truncate">{TRACE_LINES[traceIdx]}</span>
              <BlinkingCursor />
            </motion.p>
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  )
}

function Reading({ label, value, unit }: { label: string; value: string; unit?: string }) {
  return (
    <div>
      <p className="text-[9px] uppercase tracking-[0.16em] text-white/45 mb-0.5">{label}</p>
      <p className="text-[16px] font-semibold text-white tabular-nums tracking-tight leading-none">
        {value}
        {unit && <span className="text-[10px] text-emerald-400/85 ml-0.5">{unit}</span>}
      </p>
    </div>
  )
}

function BlinkingCursor() {
  return (
    <motion.span
      animate={{ opacity: [1, 0, 1] }}
      transition={{ duration: 1.0, repeat: Infinity, ease: "linear" }}
      className="inline-block w-[6px] h-[10px] bg-emerald-400 ml-0.5 align-baseline"
      aria-hidden
    />
  )
}

function clamp(n: number, min: number, max: number): number {
  if (n < min) return min
  if (n > max) return max
  return n
}
